import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { HuntingAreasRenderer } from "./map/hunting_areas_renderer.js"
import { DataFetcher } from "./map/data_fetcher.js"
import { MapConfig } from "./map/map_config.js"
import L from "leaflet"

export default class extends Controller {
  static targets = ["map"]
  static values = {
    huntingLogId: Number,
    currentUserId: Number,
    huntingAreasUrl: String,
    showHuntingAreas: Boolean,
    newSeenUrl: String
  }

  connect() {
    this.markers = {}
    this.killedGameMarkers = []
    this.lastSent = 0
    this.watchId = null
    this.subscription = null
    this.map = null

    // Connect channel immediately — independent of map init
    this._connectChannel()
    this._startLocationTracking()

    // Map needs the modal to be fully visible (dimensions must exist)
    const modal = this.element.closest(".modal")
    if (modal && !modal.classList.contains("show")) {
      modal.addEventListener("shown.bs.modal", this._onModalShown = () => this._initMap())
    } else {
      this._initMap()
    }
  }

  disconnect() {
    this._stopLocationTracking()
    if (this.subscription) this.subscription.unsubscribe()
    if (this.map) this.map.remove()
    const modal = this.element.closest(".modal")
    if (modal && this._onModalShown) {
      modal.removeEventListener("shown.bs.modal", this._onModalShown)
    }
  }

  _initMap() {
    if (!this.hasMapTarget || this.map) return

    const tileConfig = MapConfig.getTileLayerOptions()
    this.map = L.map(this.mapTarget).setView([45.815, 15.9819], 13)
    L.tileLayer(tileConfig.url, tileConfig.options).addTo(this.map)
    L.control.scale().addTo(this.map)
    this.map.invalidateSize()

    this._addSeenButton()
    if (this.hasHuntingAreasUrlValue && this.showHuntingAreasValue) this._loadHuntingAreas()
  }

  _addSeenButton() {
    if (!this.map || !this.hasNewSeenUrlValue) return

    const newSeenUrl = this.newSeenUrlValue
    const map = this.map
    const AddSeenControl = L.Control.extend({
      onAdd() {
        const btn = L.DomUtil.create("button", "leaflet-bar leaflet-control btn btn-light")
        btn.innerHTML = "+"
        btn.title = "Dodaj viđanje"
        btn.style.cssText = "width:34px;height:34px;font-size:20px;font-weight:bold;line-height:1;cursor:pointer;border:none;"
        L.DomEvent.on(btn, "click", L.DomEvent.stop)
        L.DomEvent.on(btn, "click", () => {
          const center = map.getCenter()
          const url = `${newSeenUrl}?latitude=${center.lat.toFixed(6)}&longitude=${center.lng.toFixed(6)}`
          const frame = document.getElementById("modal")
          if (frame) { frame.setAttribute("src", url) } else { window.location.href = url }
        })
        return btn
      }
    })
    new AddSeenControl({ position: "topleft" }).addTo(this.map)
  }

  _loadHuntingAreas() {
    const dataFetcher = new DataFetcher(this.map)
    const areasRenderer = new HuntingAreasRenderer(this.map, dataFetcher)
    areasRenderer.loadAndRender(this.huntingAreasUrlValue)
    this.map.on("moveend", () => areasRenderer.loadAndRender(this.huntingAreasUrlValue))
  }

  _connectChannel() {
    const consumer = createConsumer()
    this.subscription = consumer.subscriptions.create(
      { channel: "HuntingSessionChannel", hunting_log_id: this.huntingLogIdValue },
      { received: (data) => this._handleMessage(data) }
    )
  }

  _startLocationTracking() {
    if (!navigator.geolocation) return
    this.watchId = navigator.geolocation.watchPosition(
      (pos) => this._sendLocation(pos),
      null,
      { enableHighAccuracy: true, maximumAge: 10000 }
    )
  }

  _stopLocationTracking() {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId)
      this.watchId = null
    }
  }

  _sendLocation(position) {
    if (!this.subscription) return
    const now = Date.now()
    if (now - this.lastSent < 5000) return
    this.lastSent = now
    this.subscription.perform("update_location", {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy
    })
  }

  _handleMessage(data) {
    if (data.type === "location_update") {
      this._updateParticipantMarker(data)
    } else if (data.type === "participant_left") {
      this._removeParticipantMarker(data.user_id)
    } else if (data.type === "killed_game") {
      this._addKilledGameMarker(data)
    }
  }

  _updateParticipantMarker({ user_id, name, latitude, longitude }) {
    if (!this.map) return

    const isSelf = user_id === this.currentUserIdValue
    const color = isSelf ? "#206bc4" : "#e67e22"
    const initial = (name || "?")[0].toUpperCase()

    const icon = L.divIcon({
      html: `<div style="width:32px;height:32px;border-radius:50%;background:${color};color:#fff;font-weight:bold;font-size:14px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)">${initial}</div>`,
      className: "",
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    })

    if (this.markers[user_id]) {
      this.markers[user_id].setLatLng([latitude, longitude])
    } else {
      this.markers[user_id] = L.marker([latitude, longitude], { icon })
        .bindTooltip(name, { permanent: false, direction: "top" })
        .addTo(this.map)
    }

    const positions = Object.values(this.markers).map(m => m.getLatLng())
    if (positions.length > 0) {
      this.map.fitBounds(L.latLngBounds(positions), { padding: [50, 50], maxZoom: 15 })
    }
  }

  _removeParticipantMarker(userId) {
    if (this.markers[userId]) {
      this.markers[userId].remove()
      delete this.markers[userId]
    }
  }

  _addKilledGameMarker({ name, game_type, quantity, latitude, longitude }) {
    if (!this.map) return

    const label = game_type.replace(/_/g, " ")
    const icon = L.divIcon({
      html: `<div style="background:#c0392b;color:#fff;font-size:11px;font-weight:bold;padding:2px 5px;border-radius:4px;white-space:nowrap;border:1px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)">† ${label}</div>`,
      className: "",
      iconAnchor: [0, 0]
    })

    L.marker([latitude, longitude], { icon })
      .bindTooltip(`${name}: ${quantity}× ${label}`, { permanent: false, direction: "top" })
      .addTo(this.map)
  }
}
